/**
 * Application Permissions Configuration
 *
 * Defines all available permissions in the system organized by domain.
 * Used for:
 * - Route protection (PermissionProtectedRoute)
 * - UI element visibility (AccessProtected component)
 * - Navigation filtering (usePermissionFilteredNav hook)
 *
 * Format: domain.action (e.g., 'members.view', 'classes.create')
 */

export const PERMISSIONS = {
  // Core Domains
  BRANCHES: {
    VIEW: 'branches.view',
    CREATE: 'branches.create',
    UPDATE: 'branches.update',
    DELETE: 'branches.delete',
    EXPORT: 'branches.export',
  },

  MEMBERS: {
    VIEW: 'members.view',
    CREATE: 'members.create',
    UPDATE: 'members.update',
    DELETE: 'members.delete',
    BAN: 'members.ban',
    SUSPEND: 'members.suspend',
    DEACTIVATE: 'members.deactivate',
    EXPORT: 'members.export',
  },

  STAFF: {
    VIEW: 'staff.view',
    CREATE: 'staff.create',
    UPDATE: 'staff.update',
    DELETE: 'staff.delete',
    EXPORT: 'staff.export',
  },

  COACHES: {
    VIEW: 'coaches.view',
    CREATE: 'coaches.create',
    UPDATE: 'coaches.update',
    DELETE: 'coaches.delete',
    EXPORT: 'coaches.export',
  },

  MANAGERS: {
    VIEW: 'managers.view',
    CREATE: 'managers.create',
    UPDATE: 'managers.update',
    DELETE: 'managers.delete',
    EXPORT: 'managers.export',
  },

  ASSISTANTS: {
    VIEW: 'assistants.view',
    CREATE: 'assistants.create',
    UPDATE: 'assistants.update',
    DELETE: 'assistants.delete',
    EXPORT: 'assistants.export',
  },

  PLANS: {
    VIEW: 'plans.view',
    CREATE: 'plans.create',
    UPDATE: 'plans.update',
    DELETE: 'plans.delete',
    EXPORT: 'plans.export',
  },

  PAYMENTS: {
    VIEW: 'payments.view',
    CREATE: 'payments.create',
    UPDATE: 'payments.update',
    EXPORT: 'payments.export',
  },

  CLASSES: {
    VIEW: 'classes.view',
    CREATE: 'classes.create',
    UPDATE: 'classes.update',
    DELETE: 'classes.delete',
    ASSIGN_COACH: 'classes.assign_coach',
    EXPORT: 'classes.export',
  },

  BOOKINGS: {
    VIEW: 'bookings.view',
    CREATE: 'bookings.create',
    UPDATE: 'bookings.update',
    CANCEL: 'bookings.cancel',
    OVERRIDE: 'bookings.override',
  },

  HEALTH: {
    VIEW: 'health.view',
    CREATE: 'health.create',
    UPDATE: 'health.update',
    DELETE: 'health.delete',
    EXPORT: 'health.export',
  },

  CHECKINS: {
    VIEW: 'checkins.view',
    CREATE: 'checkins.create',
    UPDATE: 'checkins.update',
    DELETE: 'checkins.delete',
    EXPORT: 'checkins.export',
  },

  REPORTS: {
    GLOBAL: 'reports.global',
    BRANCH: 'reports.branch',
    MEMBERSHIP: 'reports.membership',
    FINANCES: 'reports.finances',
    BOOKINGS: 'reports.bookings',
    ATTENDANCE: 'reports.attendance',
    EXPORT: 'reports.export',
  },

  PROFILE: {
    VIEW: 'profile.view',
    UPDATE: 'profile.update',
    DELETE: 'profile.delete',
  },

  // System & Configuration
  SETTINGS: {
    VIEW: 'settings.view',
    UPDATE: 'settings.update',
  },

  NOTIFICATIONS: {
    VIEW: 'notifications.view',
    SEND: 'notifications.send',
  },

  FEEDBACK: {
    VIEW: 'feedback.view',
    CREATE: 'feedback.create',
    UPDATE: 'feedback.update',
    DELETE: 'feedback.delete',
    EXPORT: 'feedback.export',
  },

  SUPPORT: {
    VIEW: 'support.view',
    MANAGE: 'support.manage',
  },
} as const

/**
 * Flattened array of all permission strings
 * Useful for validation and autocomplete
 */
export const ALL_PERMISSIONS = Object.values(PERMISSIONS).flatMap((domain) => Object.values(domain))

/**
 * Type-safe permission string
 */
export type Permission = (typeof ALL_PERMISSIONS)[number]

/**
 * Helper to check if a string is a valid permission
 */
export function isValidPermission(permission: string): permission is Permission {
  return ALL_PERMISSIONS.includes(permission as Permission)
}

/**
 * Get all permissions for a specific domain
 * @example getPermissionsByDomain('members') // ['members.view', 'members.create', ...]
 */
export function getPermissionsByDomain(domain: keyof typeof PERMISSIONS): string[] {
  return Object.values(PERMISSIONS[domain])
}
